import React, { useEffect, useState } from "react";

import ChatPromptInput from "src/reusable/Chat/PromptInput/ChatPromptInput";
import { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import { FileWithPreview } from "src/reusable/Chat/types";
import { NucToast } from "src/reusable/components/Toast";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { ImageContent } from "src/views/assistant/types";
import { type TokenUsageData } from "src/reusable/Chat/TokenUsageGauge";

type PartsAIChatInputProps = {
  chatStreamError: string | null;
  isChatConnectionError: boolean | null;
  isChatLoading: boolean;
  isChatEnded: boolean;
  isTurnActive: boolean;
  InputButtons?: React.FC<any>;
  InputBar?: React.FC<any>;
  isSplashScreen?: boolean;
  terminateConnection: () => void;
  sendUserMessage: (text: string, images?: ImageContent[]) => Promise<boolean>;
  mergedFiles?: FileWithPreview[];
  tokenUsage?: TokenUsageData | null;
  placeholder?: string;
  initialValue?: string;
};

const DEFAULT_PLACEHOLDER = "What can Parts AI do for you?";

const PartsAIChatInput = ({
  chatStreamError,
  isChatConnectionError,
  isChatLoading,
  isTurnActive,
  InputButtons,
  InputBar,
  isSplashScreen = false,
  terminateConnection,
  sendUserMessage,
  tokenUsage,
  placeholder = DEFAULT_PLACEHOLDER,
  initialValue,
}: PartsAIChatInputProps) => {
  const [userInput, setUserInput] = useState<string>(initialValue ?? "");

  // track attached files from the chunked upload system
  const {
    uploadedFiles,
    addUploadedFile,
    removeUploadedFile,
    setSourceDataRecordIds,
  } = useChatStore();

  // The record ids this composer has put into the scope. The synchronous
  // file-list change drops an id before the next turn can send it;
  // onRemoveFile fires later, whether or not the server delete succeeded,
  // and covers an id that arrived after the last list change. Only ids
  // attached through this composer are tracked here.
  const [attachedRecordIds, setAttachedRecordIds] = useState<number[]>([]);

  const handleFilesChange = (files: FileUploadState[]) => {
    // Clear existing user-uploaded files (but NOT private dataset docs or
    // project attachments).
    uploadedFiles.forEach((file) => {
      if (!file.isFromDialogueHistory && !file.isFromProject) {
        removeUploadedFile(file.id);
      }
    });

    files.forEach((file) => addUploadedFile(file));

    // Attachments add to the document scope, they do not define it: a
    // document chosen on the splash is not this composer's to drop, and the
    // upload hook fires this with an empty list on every mount.
    const sdrIds = files
      .map((file) => file.sourceDataRecordId)
      .filter((id): id is number => id !== undefined);
    const removed = attachedRecordIds.filter((id) => !sdrIds.includes(id));
    const existing = useChatStore.getState().sourceDataRecordIds;
    setSourceDataRecordIds([
      ...new Set([
        ...existing.filter((id) => !removed.includes(id)),
        ...sdrIds,
      ]),
    ]);
    setAttachedRecordIds(sdrIds);
  };

  // The per-file removal signal covers a file whose record id arrived after
  // the last file-list change; the list change above handles the usual case.
  const handleRemoveFile = (file: FileUploadState) => {
    if (file.sourceDataRecordId === undefined) {
      return;
    }
    const existing = useChatStore.getState().sourceDataRecordIds;
    setSourceDataRecordIds(
      existing.filter((id) => id !== file.sourceDataRecordId),
    );
  };

  const handleFormSubmit = async () => {
    if (isTurnActive || isChatLoading) {
      return;
    }

    // Don't allow empty messages unless there are files attached
    if (userInput.trim() === "" && uploadedFiles.length === 0) {
      NucToast({
        message: "Please enter a message or attach files",
        type: "error",
        hideProgressBar: false,
      });
      return;
    }

    // Send the message with files (allow sending even if files are processing)
    try {
      const sentSuccessfully = await sendUserMessage(userInput);
      if (sentSuccessfully) {
        setUserInput("");
      }
    } catch (error) {
      console.error("[PartsAIChatInput] Failed to send message:", error);
      NucToast({
        message: "Failed to send message. Please try again.",
        type: "error",
        hideProgressBar: false,
      });
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(event.target.value);
  };

  const handleStopChat = () => {
    terminateConnection();
  };

  useEffect(() => {
    if (chatStreamError || isChatConnectionError) {
      terminateConnection();
    }
  }, [chatStreamError, terminateConnection, isChatConnectionError]);

  return (
    <ChatPromptInput
      onSubmit={handleFormSubmit}
      isInputting={!isTurnActive && userInput !== ""}
      isChatLoading={isChatLoading}
      isGenerating={isTurnActive}
      onStopChat={handleStopChat}
      value={userInput}
      onChange={handleChange}
      onFilesChange={handleFilesChange}
      onRemoveFile={handleRemoveFile}
      InputBar={InputBar}
      InputButtons={InputButtons}
      isSplashScreen={isSplashScreen}
      placeholder={placeholder}
      product="parts-ai"
      tokenUsage={tokenUsage}
    />
  );
};

export default PartsAIChatInput;
